/**
 * Translation API Client
 * Provides translation services for chat, secret messages, and future subtitle features.
 */

import { apiClient } from './api-client';

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || '';

// Types
export type TargetLanguage = 'JA' | 'EN';
export type SourceLanguage = 'JA' | 'EN' | 'AUTO';

export interface TranslateRequest {
    text: string;
    target_lang: TargetLanguage;
    source_lang?: SourceLanguage;
}

export interface TranslateResponse {
    translated_text: string;
    detected_source_lang: string | null;
    original_text: string;
}

export interface BatchTranslateRequest {
    texts: string[];
    target_lang: TargetLanguage;
    source_lang?: SourceLanguage;
}

export interface BatchTranslateResponse {
    translations: TranslateResponse[];
}

export interface TranslationConfigResponse {
    is_configured: boolean;
    service: string;
}

export interface UsageResponse {
    character_count: number;
    character_limit: number;
    usage_percent: number;
}

export interface ProfileTranslateRequest {
    streamer_id: number;
    fields: Record<string, string>;
    target_lang: TargetLanguage;
}

export interface ProfileTranslateResponse {
    translations: Record<string, string>;
    cached_count: number;
    translated_count: number;
}

export interface NotificationTranslateRequest {
    notifications: Array<{ id: number; title: string; message?: string }>;
    target_lang: TargetLanguage;
}

export interface NotificationTranslation {
    id: number;
    title: string;
    message?: string;
}

export interface NotificationTranslateResponse {
    translations: NotificationTranslation[];
    cached_count: number;
    translated_count: number;
}

// Helper function to get auth headers
function getAuthHeaders(): HeadersInit {
    const token = apiClient.getAccessToken();
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
}

// Translation API Client
export const translationClient = {
    /**
     * Check if translation service is configured
     */
    async isConfigured(): Promise<boolean> {
        try {
            const response = await fetch(`${API_BASE_URL}/api/translate/config`, {
                method: 'GET',
                headers: getAuthHeaders(),
                credentials: 'include',
            });

            if (!response.ok) {
                return false;
            }

            const data: TranslationConfigResponse = await response.json();
            return data.is_configured;
        } catch {
            return false;
        }
    },

    /**
     * Check if translation service is configured (public endpoint, no auth required)
     */
    async isConfiguredPublic(): Promise<boolean> {
        try {
            const response = await fetch(`${API_BASE_URL}/api/translate/public-config`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) {
                return false;
            }

            const data: TranslationConfigResponse = await response.json();
            return data.is_configured;
        } catch {
            return false;
        }
    },

    /**
     * Translate a single text
     */
    async translate(
        text: string,
        targetLang: TargetLanguage,
        sourceLang?: SourceLanguage
    ): Promise<TranslateResponse> {
        const request: TranslateRequest = {
            text,
            target_lang: targetLang,
            source_lang: sourceLang || 'AUTO',
        };

        const response = await fetch(`${API_BASE_URL}/api/translate/translate`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Translation failed' }));
            throw new Error(error.detail || 'Translation failed');
        }

        return response.json();
    },

    /**
     * Translate multiple texts in a single request
     */
    async batchTranslate(
        texts: string[],
        targetLang: TargetLanguage,
        sourceLang?: SourceLanguage
    ): Promise<TranslateResponse[]> {
        const request: BatchTranslateRequest = {
            texts,
            target_lang: targetLang,
            source_lang: sourceLang || 'AUTO',
        };

        const response = await fetch(`${API_BASE_URL}/api/translate/batch`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Batch translation failed' }));
            throw new Error(error.detail || 'Batch translation failed');
        }

        const data: BatchTranslateResponse = await response.json();
        return data.translations;
    },

    /**
     * Translate multiple texts in a single request (public endpoint, no auth required)
     * Maximum 10 texts per request
     */
    async batchTranslatePublic(
        texts: string[],
        targetLang: TargetLanguage,
        sourceLang?: SourceLanguage
    ): Promise<TranslateResponse[]> {
        const request: BatchTranslateRequest = {
            texts,
            target_lang: targetLang,
            source_lang: sourceLang || 'AUTO',
        };

        const response = await fetch(`${API_BASE_URL}/api/translate/public-batch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Batch translation failed' }));
            throw new Error(error.detail || 'Batch translation failed');
        }

        const data: BatchTranslateResponse = await response.json();
        return data.translations;
    },

    /**
     * Translate Japanese text to English
     */
    async translateToEnglish(text: string): Promise<string> {
        const result = await this.translate(text, 'EN', 'JA');
        return result.translated_text;
    },

    /**
     * Translate English text to Japanese
     */
    async translateToJapanese(text: string): Promise<string> {
        const result = await this.translate(text, 'JA', 'EN');
        return result.translated_text;
    },

    /**
     * Get API usage statistics (admin only)
     */
    async getUsage(): Promise<UsageResponse> {
        const response = await fetch(`${API_BASE_URL}/api/translate/usage`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Failed to get usage' }));
            throw new Error(error.detail || 'Failed to get usage');
        }

        return response.json();
    },

    /**
     * Translate profile fields with caching (public endpoint)
     * Maximum 5 fields per request (hobbies, favorite_food, features, bio, notice_prefix)
     */
    async translateProfilePublic(
        streamerId: number,
        fields: Record<string, string>,
        targetLang: TargetLanguage
    ): Promise<ProfileTranslateResponse> {
        const request: ProfileTranslateRequest = {
            streamer_id: streamerId,
            fields,
            target_lang: targetLang,
        };

        const response = await fetch(`${API_BASE_URL}/api/translate/public-profile`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Profile translation failed' }));
            throw new Error(error.detail || 'Profile translation failed');
        }

        return response.json();
    },

    /**
     * Translate notifications with caching (requires authentication)
     * Maximum 50 notifications per request
     */
    async translateNotifications(
        notifications: Array<{ id: number; title: string; message?: string }>,
        targetLang: TargetLanguage
    ): Promise<NotificationTranslateResponse> {
        const request: NotificationTranslateRequest = {
            notifications,
            target_lang: targetLang,
        };

        const response = await fetch(`${API_BASE_URL}/api/translate/notifications`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Notification translation failed' }));
            throw new Error(error.detail || 'Notification translation failed');
        }

        return response.json();
    },
};

export default translationClient;
